import React from "react";
import { Badge, Btn, DealStepper, ErrorState, InsightBox, Loading, Overline } from "../../../ForBrands/ui/BrandUI";
import { MobileStackHeader } from "../../../ForBrands/ui/mobile/MobileShell";
import {
  ContextStrip,
  KeyValueRow,
  ListRule,
  MobileCard,
  MobilePageHead,
  PayoutRow,
  TextLink,
} from "../../../ForBrands/ui/mobile/MobileUI";
import { HELP_ACTION } from "../../../ForBrands/ui/mobile/support";

// Athlete Mode · campaign tracking on phones (Figma "08 · Campaign Tracking"
// 2000:1033). A pushed screen: the shell draws no chrome, so this renders the
// stack header, the deal's context strip and a document-scrolled body. The
// desktop tables become row lists (deliverables, payouts), the stepper is the
// compact list, and the web's "Message {brand}" / "Review contract" buttons
// sit under the head (D-a20). "Upload new contract" and the brief's hashtags
// and target audience are not built: the web has no such data.
// AthleteTrackingPage owns the data, the handlers and the Log content modal.

const cx = (...parts) => parts.filter(Boolean).join(" ");

const TITLE = "Campaign Tracker";

const Header = ({ onBack }) => (
  <MobileStackHeader title={TITLE} leading="back" onLeading={onBack} leadingLabel="Back to My Deals" action={HELP_ACTION} />
);

const CardTitle = ({ children, className = "" }) => <h2 className={cx("bm-h-s text-bm-ink", className)}>{children}</h2>;

// Rows split by 1px rules, no gap between them (the rows carry the padding).
const RuledList = ({ items, render }) =>
  items.map((item, i) => (
    <React.Fragment key={item.id ?? i}>
      {i > 0 && <ListRule />}
      {render(item)}
    </React.Fragment>
  ));

const ContractCard = ({ view, onDownload }) => (
  <MobileCard>
    <CardTitle>Contract Management</CardTitle>
    {view.hasContract ? (
      <>
        <Overline>
          Signatures · {view.signedCount} of {view.signatures.length} complete
        </Overline>
        {/* eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets */}
        <ul role="list" className="flex flex-col gap-3">
          {view.signatures.map((s) => (
            <li key={s.name} className="flex flex-col gap-[2px] break-words">
              <span className="bm-body-m text-bm-ink">{s.name}</span>
              <span className={cx("bm-body-xs", s.at ? "text-bm-positive" : "text-bm-muted")}>
                {s.at ? `✓ Signed · ${s.atText}` : "Signature pending"}
              </span>
            </li>
          ))}
        </ul>
        <div className="flex flex-col items-start gap-2">
          <TextLink to={view.contractHref}>View signed contract</TextLink>
          <TextLink onClick={onDownload}>Download {view.contractHasFile ? "PDF" : "terms"}</TextLink>
        </div>
      </>
    ) : (
      <p className="bm-body-m text-bm-muted">The contract has not been sent yet.</p>
    )}
  </MobileCard>
);

// One deliverable (2025:1552): label, platform and due date, the status
// badge, then one action: Submit / Resubmit opens Log content, Locked is
// plain muted text, In review / Approved a muted note.
const DeliverableRow = ({ row, onLog }) => {
  const { d, badge, canLog, revision } = row;
  let action;
  if (d.status === "locked") action = <span className="bm-label-l text-bm-muted">Locked</span>;
  else if (d.status === "approved") action = <span className="bm-body-s text-bm-muted">Approved</span>;
  else if (d.status === "under_review") action = <span className="bm-body-s text-bm-muted">In review</span>;
  else {
    action = (
      <TextLink arrow={false} onClick={() => onLog(d)} disabled={!canLog} className="self-start">
        {revision ? "Resubmit" : "Submit"}
      </TextLink>
    );
  }
  return (
    <div className="flex flex-col gap-2 py-3">
      <div className="flex items-center gap-3">
        <div className="flex min-w-0 flex-1 flex-col gap-[2px] break-words">
          <span className="bm-h-xs text-bm-ink">{d.label}</span>
          <span className="bm-body-xs text-bm-muted">{[d.platform, row.due].filter(Boolean).join(" · ")}</span>
          {revision && (
            <span className="bm-body-xs text-bm-critical">Revision requested{revision.note ? `: ${revision.note}` : ""}</span>
          )}
        </div>
        <Badge tone={badge.tone} dot={badge.dot} className="shrink-0">
          {badge.label}
        </Badge>
      </div>
      {action}
    </div>
  );
};

const DeliverablesCard = ({ view, onLog }) => (
  <MobileCard>
    <CardTitle>Campaign Deliverables</CardTitle>
    {view.deliverables.length ? (
      <div className="flex flex-col">
        <RuledList items={view.deliverables} render={(row) => <DeliverableRow row={row} onLog={onLog} />} />
      </div>
    ) : (
      <p className="bm-body-m text-bm-muted">{view.noDeliverablesText}</p>
    )}
  </MobileCard>
);

// Milestone payouts (2025:1581): Paid and Upcoming read as muted text,
// Pending as an outline badge; then the four totals.
const PayoutsCard = ({ view }) => (
  <MobileCard>
    <div className="flex items-center gap-2">
      <CardTitle className="min-w-0 flex-1">Milestone Payouts</CardTitle>
      <span className="shrink-0 bm-body-xs text-bm-muted">Total Value: {view.totalValue}</span>
    </div>
    <div className="flex flex-col">
      <RuledList
        items={view.milestones}
        render={(m) => (
          <PayoutRow
            label={m.label}
            sub={m.due}
            amount={m.amount}
            badge={m.status === "pending_approval" ? m.statusLabel : undefined}
            status={m.status === "pending_approval" ? undefined : m.statusLabel}
          />
        )}
      />
      {view.milestones.length > 0 && <ListRule />}
      {view.totals.map(([label, value], i) => (
        <KeyValueRow key={label} label={label} value={value} emphasis={i === view.totals.length - 1} last={i === view.totals.length - 1} />
      ))}
    </div>
  </MobileCard>
);

const BriefCard = ({ view }) => (
  <MobileCard>
    <CardTitle>Campaign Brief</CardTitle>
    <dl className="flex flex-col gap-3">
      {view.brief.map(([label, value]) => (
        <div key={label} className="flex flex-col gap-[2px] break-words">
          <dt className="bm-overline">{label}</dt>
          <dd className="bm-body-s text-bm-ink">{value}</dd>
        </div>
      ))}
    </dl>
  </MobileCard>
);

const ActivityCard = ({ view }) => (
  <MobileCard>
    <Overline>Deal activity log</Overline>
    {view.activity.length ? (
      // eslint-disable-next-line jsx-a11y/no-redundant-roles -- VoiceOver drops the list role of a list with no bullets
      <ul role="list" className="flex flex-col gap-[10px]">
        {view.activity.map((a) => (
          <li key={a.id} className="flex items-start gap-3 text-bm-muted">
            <span className="w-[52px] shrink-0 pt-[2px] bm-label-xs">{a.date}</span>
            <span className="min-w-0 flex-1 break-words bm-body-s">{a.text}</span>
          </li>
        ))}
      </ul>
    ) : (
      <p className="bm-body-s text-bm-muted">No activity yet.</p>
    )}
  </MobileCard>
);

// `view` is null while loading or after an error: the header still renders
// so the athlete can go back.
const TrackingMobile = ({ view, error, onBack, onMessage, onDownload, onLog }) => {
  if (!view || error) {
    return (
      <>
        <Header onBack={onBack} />
        <div className="px-6 pt-5 pb-8">{error ? <ErrorState>{error}</ErrorState> : <Loading />}</div>
      </>
    );
  }

  return (
    <>
      <Header onBack={onBack} />
      <ContextStrip
        title={view.campaignName}
        badge={
          <Badge tone="outline" dot={view.active ? "#1f9d55" : undefined} className="shrink-0">
            {view.stateLabel}
          </Badge>
        }
        meta={view.contextMeta}
      />
      <div className="flex flex-col gap-[18px] px-6 pt-5 pb-8">
        <MobilePageHead eyebrow="Campaign tracker" eyebrowBrand title={view.campaignName} sub={view.headSub}>
          <Btn variant="secondary" size="lg" className="w-full" onClick={onMessage}>
            <span className="min-w-0 truncate">Message {view.company}</span>
          </Btn>
          {view.canReviewContract && (
            <Btn size="lg" className="w-full" to={view.contractHref}>
              Review contract
            </Btn>
          )}
        </MobilePageHead>

        <MobileCard>
          <CardTitle>Deal Progress</CardTitle>
          <DealStepper compact current={view.step} />
        </MobileCard>

        <ContractCard view={view} onDownload={onDownload} />
        <DeliverablesCard view={view} onLog={onLog} />
        <PayoutsCard view={view} />
        <BriefCard view={view} />

        <InsightBox compact title="Deal health insight">
          {view.health}
        </InsightBox>

        <ActivityCard view={view} />
      </div>
    </>
  );
};

export default TrackingMobile;
